var __rest = (this && this.__rest) || function (s, e) {
    var t = {};
    for (var p in s) if (Object.prototype.hasOwnProperty.call(s, p) && e.indexOf(p) < 0)
        t[p] = s[p];
    if (s != null && typeof Object.getOwnPropertySymbols === "function")
        for (var i = 0, p = Object.getOwnPropertySymbols(s); i < p.length; i++) {
            if (e.indexOf(p[i]) < 0 && Object.prototype.propertyIsEnumerable.call(s, p[i]))
                t[p[i]] = s[p[i]];
        }
    return t;
};
import { baseApi } from "../api/baseApi";
export const projectsApi = baseApi.injectEndpoints({
    endpoints: (builder) => ({
        getCompanies: builder.query({
            query: (userEmail) => ({
                url: "Project/entities/by-email",
                params: { userEmail },
            }),
            providesTags: ["Companies"],
        }),
        getAllCompanies: builder.query({
            query: () => ({
                url: "Project/entities/by-email",
                params: { userEmail: "ALL" },
            }),
            providesTags: ["Companies"],
        }),
        getProjects: builder.query({
            query: () => "Project",
            providesTags: ["Projects"],
        }),
        getProjectById: builder.query({
            query: (id) => `Project/${id}`,
            providesTags: (_result, _error, id) => [{ type: "Projects", id }],
        }),
        getProjectCostValues: builder.query({
            query: ({ projectId, projectNumber, childProjectNumber, companyId }) => ({
                url: "ProjectCostValues",
                params: { projectId, projectNumber, childProjectNumber, companyId },
            }),
            providesTags: ["ProjectCostValues"],
        }),
        createProjectCostValue: builder.mutation({
            query: (data) => ({
                url: "ProjectCostValues",
                method: "POST",
                body: data,
            }),
            invalidatesTags: ["ProjectCostValues", "Projects"],
        }),
        getProjectSnapshots: builder.mutation({
            query: (request) => {
                return {
                    url: "Project/snapshots",
                    method: "POST",
                    body: request,
                };
            },
        }),
        getProjectTree: builder.query({
            query: ({ topProjectNumber, entityName, aanneemsom, companyId }) => {
                const params = {
                    topProjectNumber,
                    entityName,
                };
                if (aanneemsom) {
                    params.aanneemsom = aanneemsom;
                }
                if (companyId) {
                    params.companyId = companyId;
                }
                return {
                    url: "Project/tree",
                    params: params,
                };
            },
            providesTags: ["Projects"],
        }),
        getCombinedMetrics: builder.query({
            query: ({ parentProjectNumber, childProjectNumber, companyId }) => ({
                url: "Project/combined-metrics",
                params: { parentProjectNumber, childProjectNumber, companyId },
            }),
            providesTags: ["Projects"],
        }),
        getForecastAdjustment: builder.query({
            query: ({ parentProjectNumber, childProjectNumber, companyId }) => ({
                url: "ForecastAdjustments/by-project",
                params: {
                    projectNumber: parentProjectNumber,
                    childProjectNumber,
                    companyId,
                },
            }),
            transformResponse: (response) => {
                return response;
            },
        }),
        createForecastAdjustment: builder.mutation({
            query: (body) => ({
                url: "ForecastAdjustments",
                method: "POST",
                body: {
                    ProjectNumber: body.projectNumber,
                    ProjectId: body.projectId,
                    ChildProjectNumber: body.childProjectNumber,
                    PocOverrule: body.pocOverrule,
                    AdjustmentCategory: body.adjustmentCategory,
                    Comment: body.comment,
                    CreatedBy: body.createdBy,
                    CompanyId: body.companyId,
                },
            }),
        }),
        updateForecastAdjustment: builder.mutation({
            query: (_a) => {
                var { id } = _a, body = __rest(_a, ["id"]);
                return ({
                    url: `ForecastAdjustments/${id}`,
                    method: "PUT",
                    body: {
                        ChildProjectNumber: body.childProjectNumber,
                        PocOverrule: body.pocOverrule,
                        AdjustmentCategory: body.adjustmentCategory,
                        Comment: body.comment,
                        ModifiedBy: body.modifiedBy,
                        CompanyId: body.companyId,
                        IsActive: body.isActive,
                        IsDeleted: body.isDeleted,
                    },
                });
            },
        }),
        getUsersSummary: builder.query({
            query: () => "CompanyUsers/summary",
            providesTags: ["Companies"],
        }),
        createUser: builder.mutation({
            query: (body) => ({
                url: "CompanyUsers",
                method: "POST",
                body: Object.assign({ companyIds: body.companyIds, userEmail: body.userEmail, username: body.username, createdBy: body.createdBy }, (body.roleId && { roleId: body.roleId })),
            }),
            invalidatesTags: ["Companies"],
        }),
        updateUserStatus: builder.mutation({
            query: ({ userId, body }) => ({
                url: `CompanyUsers/${userId}/status`,
                method: "PATCH",
                body: {
                    status: body.status,
                    modifiedBy: body.modifiedBy,
                },
            }),
            invalidatesTags: ["Companies"],
        }),
        deleteUser: builder.mutation({
            query: ({ userId, modifiedBy }) => ({
                url: `CompanyUsers/${userId}`,
                method: "DELETE",
                body: {
                    modifiedBy,
                },
            }),
            invalidatesTags: ["Companies"],
        }),
        updateUser: builder.mutation({
            query: ({ email, body }) => ({
                url: `CompanyUsers/${email}`,
                method: "PUT",
                body: body,
            }),
            invalidatesTags: ["Companies"],
        }),
        // Roles APIs
        getAllRoles: builder.query({
            query: () => "Roles",
            providesTags: ["Roles"],
        }),
        createRole: builder.mutation({
            query: (body) => ({
                url: "Roles",
                method: "POST",
                body,
            }),
            invalidatesTags: ["Roles"],
        }),
        updateRole: builder.mutation({
            query: ({ roleId, body }) => ({
                url: `Roles/${roleId}`,
                method: "PUT",
                body,
            }),
            invalidatesTags: ["Roles"],
        }),
        deleteRole: builder.mutation({
            query: ({ roleId, modifiedBy }) => ({
                url: `Roles/${roleId}`,
                method: "DELETE",
                body: {
                    modifiedBy,
                },
            }),
            invalidatesTags: ["Roles"],
        }),
        getUserPermissions: builder.query({
            query: (email) => ({
                url: "Roles/user-permissions",
                params: { email },
            }),
        }),
        getRolePermissions: builder.query({
            query: (roleId) => ({
                url: `Roles/${roleId}/permissions`,
            }),
            providesTags: (_result, _error, roleId) => [
                { type: "Roles", id: roleId },
            ],
        }),
        saveProjectMetric: builder.mutation({
            query: (body) => ({
                url: "ProjectMetricSaves",
                method: "POST",
                body,
            }),
        }),
    }),
});
export const { useGetCompaniesQuery, useGetAllCompaniesQuery, useGetProjectsQuery, useGetProjectByIdQuery, useGetProjectSnapshotsMutation, useGetProjectTreeQuery, useGetCombinedMetricsQuery, useGetForecastAdjustmentQuery, useCreateForecastAdjustmentMutation, useUpdateForecastAdjustmentMutation, useGetUsersSummaryQuery, useGetProjectCostValuesQuery, useCreateProjectCostValueMutation, useCreateUserMutation, useUpdateUserStatusMutation, useDeleteUserMutation, useUpdateUserMutation, useGetAllRolesQuery, useCreateRoleMutation, useUpdateRoleMutation, useDeleteRoleMutation, useGetUserPermissionsQuery, useGetRolePermissionsQuery, useSaveProjectMetricMutation, } = projectsApi;
